import React, { useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';
import { CheckCircleIcon, XCircleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

export const ToastContainer: React.FC = () => {
  let toasts: Array<{ id: string; message: string; type?: 'success' | 'error' | 'info'; duration?: number; action?: { label: string; onClick: () => void } }> = [];
  let removeToast: (id: string) => void = () => {};
  
  try {
    const toastContext = useToast();
    toasts = toastContext.toasts;
    removeToast = toastContext.removeToast;
  } catch {
    // Toast context not available (SSR or not wrapped in provider)
    return null;
  }

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 top-6 left-6 md:top-auto md:left-auto z-[60] flex flex-col gap-3 pointer-events-none items-center md:items-end">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
};

interface ToastItemProps {
  toast: {
    id: string;
    message: string;
    type?: 'success' | 'error' | 'info';
    duration?: number;
    action?: {
      label: string;
      onClick: () => void;
    };
  };
  onClose: () => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onClose }) => {
  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(onClose, toast.duration);
      return () => clearTimeout(timer);
    }
  }, [toast.duration, onClose]);

  const typeStyles = {
    success: {
      bg: 'bg-green-500 dark:bg-green-600',
      text: 'text-white',
      icon: <CheckCircleIcon className="h-5 w-5 text-white" />,
      shadow: 'shadow-lg shadow-green-500/30 dark:shadow-green-600/30',
    },
    error: {
      bg: 'bg-red-500 dark:bg-red-600',
      text: 'text-white',
      icon: <XCircleIcon className="h-5 w-5 text-white" />,
      shadow: 'shadow-lg shadow-red-500/30 dark:shadow-red-600/30',
    },
    info: {
      bg: 'bg-blue-500 dark:bg-blue-600',
      text: 'text-white',
      icon: <InformationCircleIcon className="h-5 w-5 text-white" />,
      shadow: 'shadow-lg shadow-blue-500/30 dark:shadow-blue-600/30',
    },
  };

  const style = typeStyles[toast.type || 'success'];

  return (
    <div
      className={`
        ${style.bg} ${style.text} ${style.shadow}
        backdrop-blur-xl rounded-3xl
        px-4 py-3 sm:px-5 sm:py-4 min-w-[280px] sm:min-w-[300px] max-w-md w-full md:w-auto
        pointer-events-auto
        flex items-start gap-3
        border border-white/20 dark:border-white/10
        transition-all duration-300 ease-out
        animate-in slide-in-from-top-2 md:slide-in-from-bottom-2 fade-in
      `}
      role="alert"
      style={{
        backdropFilter: 'blur(24px) saturate(180%)',
        WebkitBackdropFilter: 'blur(24px) saturate(180%)',
      }}
    >
      <div className="flex-shrink-0 mt-0.5">
        {style.icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm sm:text-base font-semibold leading-tight">
          {toast.message}
        </div>
        {toast.action && (
          <button
            onClick={() => {
              toast.action?.onClick();
              onClose();
            }}
            className="mt-2.5 w-full sm:w-auto px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors active:scale-95 cursor-pointer"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        onClick={onClose}
        className="flex-shrink-0 text-white/80 hover:text-white transition-colors active:scale-95 mt-0.5"
        aria-label="Close"
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
    </div>
  );
};

